/**
 * What actually went out: which report, to whom, and when it landed.
 *
 * A failed row is a delivery the provider refused or a run that did not finish,
 * and the row says which. A run that was kept and not emailed says that too,
 * instead of printing an empty recipient.
 */
import { useMemo } from 'react'
import { deploymentNotice } from './attention'
import { Panel } from './ui'
import { DeliveryTable } from './DeliveryTable'
import { matchesDeliveryQuery, type DeliveryFilter } from './deliveries'
import type { ReportActions } from './useReportActions'
import type { ReportingData, ReportPermissions } from './useReportingData'

export function DeliveriesTab({
  data,
  actions,
  perms,
  query,
  filter,
  onFilter,
}: {
  data: ReportingData
  actions: ReportActions
  perms: ReportPermissions
  query: string
  filter: DeliveryFilter
  onFilter: (filter: DeliveryFilter) => void
}) {
  const rows = useMemo(() => data.deliveries.filter((row) => matchesDeliveryQuery(row, query)), [data.deliveries, query])
  const notice = deploymentNotice(data.status)
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-10 pt-5 sm:px-6">
      <p className="max-w-3xl text-[13px] text-[#9A9A9A]">
        What actually went out: which report, to whom, and when it landed. A failed row is a delivery the mail
        provider refused or a run that did not finish; the row says which.
      </p>
      {notice && notice.tone !== 'ok' ? (
        <Panel tone="gold" className="px-4 py-2.5 text-[12.5px] text-[#B9B9B9]">
          <span className="font-semibold text-[#F5F3EE]">{notice.title}.</span> {notice.body}
        </Panel>
      ) : null}
      <DeliveryTable
        rows={rows}
        history={data.delivery}
        loading={data.deliveryLoading}
        error={data.deliveryError}
        onRetry={data.retryDeliveries}
        filter={filter}
        onFilter={onFilter}
        now={data.now}
        actions={actions}
        perms={perms}
        label="Delivery history"
        emptyTitle={query.trim() ? 'No delivery matches' : 'Nothing has been delivered yet'}
        emptyBody={
          query.trim()
            ? 'Clear the search to see every delivery.'
            : 'When a schedule runs, each run and each email it sends is recorded here.'
        }
      />
    </div>
  )
}
